import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {Button, createStyles, keyframes} from '@mantine/core';
import {useId, useMediaQuery} from '@mantine/hooks';
import {captureException} from '@sentry/browser';
import React, {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import AIButton from 'llm/chat-library/ui/AIButton';
import {CHAT_INPUT_PLACEHOLDER_TEXT_STYLE} from 'llm/chat-library/ui/AIStyleConstants';
import {VoiceUIController, VoiceUIState} from 'llm/realtime-voice/VoiceUIController';

interface VoiceInputStateConfig {
  visibleText: string;
  accessibleText: string;
  active: boolean;
  showStop: boolean;
  showListeningHalo: boolean;
  showAgentWave: boolean;
}

const VOICE_INPUT_STATE_CONFIG: Record<VoiceUIState, VoiceInputStateConfig> = {
  [VoiceUIState.IDLE]: {
    visibleText: '',
    accessibleText: 'Voice chat ready',
    active: false,
    showStop: false,
    showListeningHalo: false,
    showAgentWave: false,
  },
  [VoiceUIState.CONNECTING]: {
    visibleText: 'Ask questions about your data…',
    accessibleText: 'Starting voice chat',
    active: true,
    showStop: true,
    showListeningHalo: false,
    showAgentWave: false,
  },
  [VoiceUIState.LISTENING]: {
    visibleText: 'Listening…',
    accessibleText: 'Listening',
    active: true,
    showStop: true,
    showListeningHalo: true,
    showAgentWave: false,
  },
  [VoiceUIState.AGENT_SPEAKING]: {
    visibleText: '',
    accessibleText: 'Responding',
    active: true,
    showStop: true,
    showListeningHalo: false,
    showAgentWave: true,
  },
};

export function isVoiceInputActive(state: VoiceUIState): boolean {
  return VOICE_INPUT_STATE_CONFIG[state].active;
}

const moveComet = keyframes({
  from: {strokeDashoffset: 0},
  to: {strokeDashoffset: -100},
});

const exitAgentDot = keyframes({
  '0%': {opacity: 1, transform: 'translateY(0)'},
  '22%': {opacity: 1, transform: 'translateY(0)'},
  '60%': {opacity: 0, transform: 'translateY(28px)'},
  '100%': {opacity: 0, transform: 'translateY(40px)'},
});

const pulseBar = keyframes({
  '0%, 100%': {transform: 'scaleY(0.45)'},
  '50%': {transform: 'scaleY(1)'},
});

const AGENT_WAVE_CYCLE_MS = 3_000;
const AGENT_DOT_EXIT_MS = 850;
const AGENT_DOT_EXIT_STAGGER_MS = 35;
const AGENT_WAVE_EXIT_MS = AGENT_DOT_EXIT_MS + 4 * AGENT_DOT_EXIT_STAGGER_MS;
const LISTENING_HALO_INITIAL_LEVEL = 0.5;
const LISTENING_HALO_HIGH_BURST_PROBABILITY = 0.25;
const LISTENING_HALO_HIGH_BURST_MIN_LEVEL = 0.75;
const LISTENING_HALO_SIMULATION_INTERVAL_MIN_MS = 150;
const LISTENING_HALO_SIMULATION_INTERVAL_RANGE_MS = 200;
const LISTENING_HALO_MIN_OPACITY = 0.4;
const LISTENING_HALO_OPACITY_RANGE = 0.25;
const LISTENING_HALO_MAX_OPACITY = 0.9;
const LISTENING_HALO_MIN_SCALE = 0.75;
const LISTENING_HALO_SCALE_RANGE = 0.85;
const LISTENING_HALO_ENTER_MS = 320;
const LISTENING_HALO_EXIT_MS = 380;

function getAgentWaveOffset(timeMs: number, index: number): number {
  return Math.sin((timeMs / AGENT_WAVE_CYCLE_MS) * Math.PI * 2 - index * 0.9) * 10;
}

function getSettlingScale(progress: number): number {
  const clampedProgress = Math.max(0, Math.min(1, progress));
  const smoothProgress = clampedProgress * clampedProgress * (3 - 2 * clampedProgress);
  return 1 - smoothProgress;
}

/**
 * Produces mostly low levels with occasional high bursts so the prototype halo reads as speech,
 * rather than a uniform pulse, when a real microphone level is unavailable.
 */
function getSimulatedListeningLevel(): number {
  const isHighBurst = Math.random() < LISTENING_HALO_HIGH_BURST_PROBABILITY;
  return isHighBurst
    ? LISTENING_HALO_HIGH_BURST_MIN_LEVEL + Math.random() * (1 - LISTENING_HALO_HIGH_BURST_MIN_LEVEL)
    : Math.random() * LISTENING_HALO_HIGH_BURST_MIN_LEVEL;
}

function getListeningHaloSimulationDelay(): number {
  return (
    LISTENING_HALO_SIMULATION_INTERVAL_MIN_MS + Math.random() * LISTENING_HALO_SIMULATION_INTERVAL_RANGE_MS
  );
}

function getListeningHaloOpacity(level: number): number {
  if (level >= LISTENING_HALO_HIGH_BURST_MIN_LEVEL) {
    return LISTENING_HALO_MAX_OPACITY;
  }
  return (
    LISTENING_HALO_MIN_OPACITY + (level / LISTENING_HALO_HIGH_BURST_MIN_LEVEL) * LISTENING_HALO_OPACITY_RANGE
  );
}

const useStyles = createStyles(() => ({
  activeRoot: {
    position: 'relative',
    isolation: 'isolate',
    boxSizing: 'border-box',
    display: 'flex',
    width: '100%',
    minHeight: 106,
    padding: 1,
    overflow: 'visible',
    borderRadius: 24,
    backgroundColor: Color.WHITE,
    boxShadow: '0 0 12px rgba(0, 13, 181, 0.10)',
  },
  activeBorder: {
    position: 'absolute',
    zIndex: 3,
    inset: 0,
    width: '100%',
    height: '100%',
    overflow: 'visible',
    pointerEvents: 'none',
  },
  comet: {
    animation: `${moveComet} 1.4s cubic-bezier(.4,.3,.6,.7) infinite`,
  },
  staticMotion: {animation: 'none !important', transition: 'none !important'},
  surface: {
    position: 'relative',
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'hidden',
    borderRadius: 23,
    backgroundColor: Color.WHITE,
  },
  state: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    minHeight: 48,
    padding: '10px 16px',
  },
  visibleState: {
    position: 'relative',
    zIndex: 2,
    ...CHAT_INPUT_PLACEHOLDER_TEXT_STYLE,
    transition: 'opacity 220ms ease, transform 220ms ease',
  },
  listeningHalo: {
    position: 'absolute',
    zIndex: 0,
    top: -42,
    right: '6%',
    left: '6%',
    height: 86,
    opacity: 0,
    background: 'linear-gradient(120deg, #3C65FB, #9775FA, #E870C0)',
    borderRadius: '50%',
    filter: 'blur(34px)',
    transform: 'translate3d(0, 0, 0)',
    pointerEvents: 'none',
  },
  listeningHaloActive: {
    opacity: 0.9,
  },
  controls: {
    position: 'relative',
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '8px 12px 11px',
  },
  leading: {display: 'flex', flex: 1, alignItems: 'center', minWidth: 0},
  trailing: {display: 'flex', alignItems: 'center', gap: 8},
  agentWave: {
    position: 'absolute',
    zIndex: 1,
    bottom: 'calc(100% + 28px)',
    left: '50%',
    display: 'flex',
    gap: 10,
    transform: 'translateX(-50%)',
    pointerEvents: 'none',
  },
  agentDotStage: {
    opacity: 0,
    transform: 'translateY(64px)',
    transition: 'opacity 550ms cubic-bezier(.18,.8,.24,1), transform 550ms cubic-bezier(.18,.8,.24,1)',
  },
  agentDotStageActive: {opacity: 1, transform: 'translateY(0)'},
  agentDotStageExiting: {
    animation: `${exitAgentDot} ${AGENT_DOT_EXIT_MS}ms cubic-bezier(.4, 0, .2, 1) both`,
  },
  agentDot: {
    display: 'block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: Color.BLUE_400,
    willChange: 'transform',
  },
  stopButton: {
    minHeight: 40,
    height: 40,
    padding: '0 13px',
    border: 0,
    borderRadius: 12,
    backgroundColor: Color.BLUE_400,
    color: Color.WHITE,
    fontSize: 13,
    '&:hover': {backgroundColor: Color.BLUE_450},
  },
  stopIcon: {display: 'inline-flex', alignItems: 'center', gap: 2, marginRight: 7},
  stopBar: {
    display: 'block',
    width: 2,
    height: 12,
    borderRadius: 1,
    backgroundColor: 'currentColor',
    animation: `${pulseBar} .9s ease-in-out infinite`,
  },
  idleButton: {width: 40, minWidth: 40, height: 40, minHeight: 40, padding: 0, borderRadius: 12},
  screenReaderOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
}));

export interface VoiceInputControlProps {
  controller: VoiceUIController;
  disabled?: boolean;
  leadingControl?: ReactNode;
  interruptControl?: ReactNode;
  onStartError?: (error: unknown) => void;
  onStopRequested?: () => void;
  reducedMotionOverride?: boolean;
}

function usePrefersReducedMotion(override?: boolean): boolean {
  const systemPreference = useMediaQuery('(prefers-reduced-motion: reduce)', undefined, {
    getInitialValueInEffect: false,
  });
  return override ?? systemPreference;
}

function ListeningHalo({
  active,
  reducedMotion,
  inputLevel,
}: {
  active: boolean;
  reducedMotion: boolean;
  /** Future microphone-level seam. Omit to use the prototype's simulated level. */
  inputLevel?: number;
}): JSX.Element {
  const {classes, cx} = useStyles();
  const [simulatedLevel, setSimulatedLevel] = useState(LISTENING_HALO_INITIAL_LEVEL);

  useEffect(() => {
    if (!active || reducedMotion || inputLevel != null) {
      return;
    }
    let timerId: number;
    const updateLevel = (): void => {
      setSimulatedLevel(getSimulatedListeningLevel());
      timerId = window.setTimeout(updateLevel, getListeningHaloSimulationDelay());
    };
    timerId = window.setTimeout(updateLevel, getListeningHaloSimulationDelay());
    return () => {
      window.clearTimeout(timerId);
    };
  }, [active, inputLevel, reducedMotion]);

  const level = Math.max(0, Math.min(1, inputLevel ?? simulatedLevel));
  const opacity = getListeningHaloOpacity(level);
  return (
    <span
      aria-hidden={true}
      className={cx(
        classes.listeningHalo,
        active && classes.listeningHaloActive,
        reducedMotion && classes.staticMotion
      )}
      data-active={active}
      data-motion={reducedMotion ? 'static' : 'animated'}
      {...getTestProps({component: 'VoiceInputControl', element: 'listeningHalo'})}
      style={{
        opacity: active ? opacity : 0,
        transform: reducedMotion
          ? 'none'
          : `translate3d(0, 0, 0) scaleY(${LISTENING_HALO_MIN_SCALE + level * LISTENING_HALO_SCALE_RANGE})`,
        transition: reducedMotion
          ? 'none'
          : `opacity ${
              active ? LISTENING_HALO_ENTER_MS : LISTENING_HALO_EXIT_MS
            }ms ease, transform ${LISTENING_HALO_ENTER_MS}ms cubic-bezier(.4, 0, .2, 1)`,
      }}
    />
  );
}

function LiveListeningHalo({
  controller,
  reducedMotion,
}: {
  controller: VoiceUIController;
  reducedMotion: boolean;
}): JSX.Element {
  const [inputLevel, setInputLevel] = useState(controller.microphoneLevel.getSnapshot);
  useEffect(
    () =>
      controller.microphoneLevel.subscribe(() => {
        setInputLevel(controller.microphoneLevel.getSnapshot());
      }),
    [controller.microphoneLevel]
  );
  return (
    <ListeningHalo
      active={controller.isUserSpeaking || inputLevel > 0.02}
      inputLevel={inputLevel}
      reducedMotion={reducedMotion}
    />
  );
}

function AgentWave({
  active,
  reducedMotion,
  sessionActive,
}: {
  active: boolean;
  reducedMotion: boolean;
  sessionActive: boolean;
}): JSX.Element {
  const {classes, cx} = useStyles();
  const dotRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const wasActiveRef = useRef(active);
  const [isExiting, setIsExiting] = useState(false);
  const shouldStartExit = !active && wasActiveRef.current;
  const showExit = isExiting || shouldStartExit;

  useLayoutEffect(() => {
    if (active) {
      setIsExiting(false);
    } else if (wasActiveRef.current) {
      setIsExiting(true);
    }
    wasActiveRef.current = active;
  }, [active]);

  useEffect(() => {
    if (!isExiting) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setIsExiting(false);
    }, AGENT_WAVE_EXIT_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isExiting]);

  useEffect(() => {
    if (reducedMotion || (!active && !showExit)) {
      dotRefs.current.forEach((dot) => {
        if (dot != null) {
          dot.style.transform = 'translateY(0)';
        }
      });
      return;
    }

    let frameId: number;
    if (active) {
      const animate = (timeMs: number): void => {
        dotRefs.current.forEach((dot, index) => {
          if (dot != null) {
            dot.style.transform = `translateY(${getAgentWaveOffset(timeMs, index)}px)`;
          }
        });
        frameId = window.requestAnimationFrame(animate);
      };
      frameId = window.requestAnimationFrame(animate);
      return () => {
        window.cancelAnimationFrame(frameId);
      };
    }

    let exitStartedAtMs: number | null = null;
    const settle = (timeMs: number): void => {
      exitStartedAtMs ??= timeMs;
      const elapsedMs = timeMs - exitStartedAtMs;
      dotRefs.current.forEach((dot, index) => {
        if (dot != null) {
          const settleProgress = (elapsedMs - index * AGENT_DOT_EXIT_STAGGER_MS) / AGENT_DOT_EXIT_MS;
          const offsetPx = getAgentWaveOffset(timeMs, index) * getSettlingScale(settleProgress);
          dot.style.transform = `translateY(${offsetPx}px)`;
        }
      });

      const finalExitMs = AGENT_DOT_EXIT_MS + (dotRefs.current.length - 1) * AGENT_DOT_EXIT_STAGGER_MS;
      if (elapsedMs < finalExitMs) {
        frameId = window.requestAnimationFrame(settle);
      } else {
        dotRefs.current.forEach((dot) => {
          if (dot != null) {
            dot.style.transform = 'translateY(0)';
          }
        });
      }
    };
    frameId = window.requestAnimationFrame(settle);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [active, reducedMotion, showExit]);

  if (!sessionActive && !active && !showExit) {
    return <></>;
  }

  return (
    <span
      aria-hidden={true}
      className={classes.agentWave}
      data-active={active}
      data-motion={reducedMotion ? 'static' : 'animated'}
      {...getTestProps({component: 'VoiceInputControl', element: 'agentWave'})}
    >
      {Array.from({length: 5}, (_, index) => {
        const stageStyle: CSSProperties = active
          ? {transitionDelay: `${index * 90}ms`}
          : {animationDelay: `${index * AGENT_DOT_EXIT_STAGGER_MS}ms`};
        return (
          <span
            data-amp-safe={true}
            {...getTestProps({component: 'VoiceInputControl', element: 'agentDotStage', key: String(index)})}
            className={cx(
              classes.agentDotStage,
              active && classes.agentDotStageActive,
              showExit && classes.agentDotStageExiting,
              reducedMotion && classes.staticMotion
            )}
            key={index}
            style={stageStyle}
          >
            <span
              {...getTestProps({component: 'VoiceInputControl', element: 'agentDot'})}
              className={classes.agentDot}
              ref={(element) => {
                dotRefs.current[index] = element;
              }}
            />
          </span>
        );
      })}
    </span>
  );
}

export function VoiceInputDecorations({
  controller,
  reducedMotionOverride,
}: Pick<VoiceInputControlProps, 'controller' | 'reducedMotionOverride'>): JSX.Element {
  const {classes, cx} = useStyles();
  const gradientId = useId();
  const reducedMotion = usePrefersReducedMotion(reducedMotionOverride);
  const sessionActive = isVoiceInputActive(controller.state);

  return (
    <>
      {sessionActive && (
        <>
          <svg
            aria-hidden={true}
            className={classes.activeBorder}
            data-motion={reducedMotion ? 'static' : 'animated'}
            {...getTestProps({component: 'VoiceInputControl', element: 'activeBorder'})}
          >
            <defs>
              <linearGradient
                {...getTestProps({component: 'VoiceInputControl', element: 'gradient'})}
                id={gradientId}
                x1="0"
                x2="1"
                y1="0"
                y2="0"
              >
                <stop
                  data-amp-safe={true}
                  {...getTestProps({component: 'VoiceInputControl', element: 'gradientStop', key: '0'})}
                  offset="34%"
                  stopColor="#3C65FB"
                />
                <stop
                  data-amp-safe={true}
                  {...getTestProps({component: 'VoiceInputControl', element: 'gradientStop', key: '1'})}
                  offset="48%"
                  stopColor="#9775FA"
                />
                <stop
                  data-amp-safe={true}
                  {...getTestProps({component: 'VoiceInputControl', element: 'gradientStop', key: '2'})}
                  offset="75%"
                  stopColor="#E870C0"
                />
                {!reducedMotion && (
                  <animateTransform
                    {...getTestProps({component: 'VoiceInputControl', element: 'gradientMotion'})}
                    attributeName="gradientTransform"
                    dur="1.4s"
                    from="0 .5 .5"
                    repeatCount="indefinite"
                    to="-360 .5 .5"
                    type="rotate"
                  />
                )}
              </linearGradient>
            </defs>
            <rect
              {...getTestProps({component: 'VoiceInputControl', element: 'comet'})}
              className={cx(classes.comet, reducedMotion && classes.staticMotion)}
              fill="none"
              height="calc(100% - 1px)"
              pathLength="100"
              rx="24"
              stroke={`url(#${gradientId})`}
              strokeDasharray="32 68"
              strokeWidth="1"
              width="calc(100% - 1px)"
              x=".5"
              y=".5"
            />
          </svg>
          <LiveListeningHalo controller={controller} reducedMotion={reducedMotion} />
        </>
      )}
      <AgentWave
        active={controller.isAgentSpeaking}
        reducedMotion={reducedMotion}
        sessionActive={sessionActive}
      />
    </>
  );
}

export function VoiceInputStatus({
  controller,
  reducedMotionOverride,
}: Pick<VoiceInputControlProps, 'controller' | 'reducedMotionOverride'>): JSX.Element {
  const {classes, cx} = useStyles();
  const reducedMotion = usePrefersReducedMotion(reducedMotionOverride);
  const config = VOICE_INPUT_STATE_CONFIG[controller.state];
  return (
    <div className={classes.state}>
      <span
        aria-hidden={true}
        className={cx(classes.visibleState, reducedMotion && classes.staticMotion)}
        data-transition={reducedMotion ? 'none' : 'animated'}
        {...getTestProps({component: 'VoiceInputControl', element: 'visibleState'})}
      >
        {config.visibleText}
      </span>
      <span aria-atomic={true} aria-live="polite" className={classes.screenReaderOnly} role="status">
        {config.accessibleText}
      </span>
    </div>
  );
}

export function VoiceInputAction({
  controller,
  disabled = false,
  onStartError,
  onStopRequested,
  reducedMotionOverride,
}: Omit<VoiceInputControlProps, 'leadingControl' | 'interruptControl'>): JSX.Element {
  const {classes, cx} = useStyles();
  const stopButtonRef = useRef<HTMLButtonElement>(null);
  const reducedMotion = usePrefersReducedMotion(reducedMotionOverride);
  const config = VOICE_INPUT_STATE_CONFIG[controller.state];
  useEffect(() => {
    if (config.active) {
      stopButtonRef.current?.focus({preventScroll: true});
    }
  }, [config.active]);
  const handleStart = useCallback(() => {
    controller.start().catch((error: unknown) => onStartError?.(error));
  }, [controller, onStartError]);
  const handleStop = useCallback(() => {
    onStopRequested?.();
    controller.stop().catch(captureException);
  }, [controller, onStopRequested]);

  if (!config.active) {
    return (
      <AIButton
        ariaLabel="Start voice chat"
        className={classes.idleButton}
        disabled={disabled}
        icon="waveform-lines"
        onClick={handleStart}
      />
    );
  }

  return (
    <Button
      aria-label="Stop voice chat"
      className={classes.stopButton}
      compact={true}
      ref={stopButtonRef}
      leftIcon={
        <span aria-hidden={true} className={classes.stopIcon}>
          {[6, 12, 8, 14, 7].map((height, index) => (
            <span
              className={cx(classes.stopBar, reducedMotion && classes.staticMotion)}
              key={index}
              style={{animationDelay: `${index * 90}ms`, height}}
            />
          ))}
        </span>
      }
      onClick={handleStop}
    >
      Stop
    </Button>
  );
}

export default function VoiceInputControl(props: VoiceInputControlProps): JSX.Element {
  const {controller, leadingControl, interruptControl, reducedMotionOverride} = props;
  const {classes} = useStyles();
  if (!isVoiceInputActive(controller.state)) {
    return <VoiceInputAction {...props} />;
  }
  return (
    <div aria-label="Voice chat active" className={classes.activeRoot} role="group">
      <VoiceInputDecorations controller={controller} reducedMotionOverride={reducedMotionOverride} />
      <div className={classes.surface}>
        <VoiceInputStatus controller={controller} reducedMotionOverride={reducedMotionOverride} />
        <div className={classes.controls}>
          <div className={classes.leading}>{leadingControl}</div>
          <div className={classes.trailing}>
            {interruptControl}
            <VoiceInputAction {...props} />
          </div>
        </div>
      </div>
    </div>
  );
}
